import React, { useState, useEffect, useRef } from 'react';
import { Network } from 'vis-network';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';
import { useNeo4j } from '../hooks/useNeo4j';

function VisualizationScreen() {
  const { dispatch } = useApp();
  const { fetchGraphData } = useNeo4j();
  const [isLoading, setIsLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [status, setStatus] = useState('Loading network data...');
  const [filters, setFilters] = useState({
    showPersons: true,
    showMissions: true,
    showSkills: true,
    showDomains: true,
    showMotivations: true,
    showRhythms: true,
    showCommunication: true,
    showConstraints: true,
    showGoals: true,
    showLanguages: true,
    showInterests: true,
    selectedPerson: 'all'
  });
  const networkRef = useRef(null);
  const networkInstance = useRef(null);

  const loadGraphData = React.useCallback(async () => {
    setIsLoading(true);
    setStatus('Fetching data from Neo4j...');
    
    try {
      console.log('=== FETCHING GRAPH VISUALIZATION DATA ===');
      const data = await fetchGraphData();
      console.log('Graph data received:', data);
      
      setGraphData(data);
      setFilteredData(data);
      setStatus(`Loaded ${data.nodes.length} nodes and ${data.edges.length} relationships`);
      
      // Initialize the network after data is loaded
      setTimeout(() => {
        initializeNetwork(data);
      }, 100);
      
    } catch (error) {
      console.error('Failed to load graph data:', error);
      setStatus(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [fetchGraphData]);

  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  // Filter data based on current filter settings
  const applyFilters = React.useCallback((data, filterSettings) => {
    if (!data) return null;

    const { 
      showPersons, showMissions, showSkills, showDomains, showMotivations,
      showRhythms, showCommunication, showConstraints, showGoals, 
      showLanguages, showInterests, selectedPerson 
    } = filterSettings;
    
    let filteredNodes = data.nodes.filter(node => {
      // Filter by node type
      if (node.group === 'person' && !showPersons) return false;
      if (node.group === 'mission' && !showMissions) return false;
      if (node.group === 'skill' && !showSkills) return false;
      if (node.group === 'domain' && !showDomains) return false;
      if (node.group === 'motivation' && !showMotivations) return false;
      if (node.group === 'rhythm' && !showRhythms) return false;
      if (node.group === 'communication' && !showCommunication) return false;
      if (node.group === 'constraint' && !showConstraints) return false;
      if (node.group === 'goal' && !showGoals) return false;
      if (node.group === 'language' && !showLanguages) return false;
      if (node.group === 'interest' && !showInterests) return false;
      
      return true;
    });

    // If specific person is selected, only show that person and their connections
    if (selectedPerson !== 'all') {
      const connectedNodeIds = new Set([selectedPerson]);
      
      // Find all edges connected to the selected person
      const connectedEdges = data.edges.filter(edge => 
        edge.from === selectedPerson || edge.to === selectedPerson
      );
      
      // Add all connected node IDs
      connectedEdges.forEach(edge => {
        connectedNodeIds.add(edge.from);
        connectedNodeIds.add(edge.to);
      });
      
      // Filter nodes to only include connected ones
      filteredNodes = filteredNodes.filter(node => connectedNodeIds.has(node.id));
    }

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    
    // Filter edges to only include those between visible nodes
    const filteredEdges = data.edges.filter(edge => 
      filteredNodeIds.has(edge.from) && filteredNodeIds.has(edge.to)
    );

    return {
      nodes: filteredNodes,
      edges: filteredEdges
    };
  }, []);

  // Apply filters when filters change
  useEffect(() => {
    if (graphData) {
      const filtered = applyFilters(graphData, filters);
      setFilteredData(filtered);
      setStatus(`Showing ${filtered.nodes.length} nodes and ${filtered.edges.length} relationships`);
      
      // Update network with filtered data
      if (networkInstance.current && filtered) {
        networkInstance.current.setData(filtered);
      }
    }
  }, [graphData, filters, applyFilters]);

  const initializeNetwork = (data) => {
    if (!networkRef.current || !data) return;

    const options = {
      nodes: {
        shape: 'dot',
        size: 20,
        font: {
          size: 14,
          color: '#ffffff'
        },
        borderWidth: 2,
        shadow: true
      },
      edges: {
        width: 2,
        color: { color: '#848484', highlight: '#34D399' },
        arrows: {
          to: { enabled: true, scaleFactor: 1 }
        },
        font: {
          size: 12,
          color: '#cccccc',
          strokeWidth: 2,
          strokeColor: '#000000'
        },
        smooth: {
          type: 'continuous'
        }
      },
      physics: {
        enabled: true,
        stabilization: { iterations: 100 },
        barnesHut: {
          gravitationalConstant: -2000,
          centralGravity: 0.3,
          springLength: 200,
          springConstant: 0.04,
          damping: 0.09
        }
      },
      interaction: {
        hover: true,
        tooltipDelay: 200,
        hideEdgesOnDrag: true
      },
      layout: {
        improvedLayout: true
      }
    };

    // Destroy existing network
    if (networkInstance.current) {
      networkInstance.current.destroy();
    }

    // Create new network
    networkInstance.current = new Network(networkRef.current, data, options);

    // Add event listeners
    networkInstance.current.on('click', (params) => {
      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        const node = data.nodes.find(n => n.id === nodeId);
        console.log('Clicked node:', node);
      }
    });

    networkInstance.current.on('hoverNode', (params) => {
      const node = data.nodes.find(n => n.id === params.node);
      if (node) {
        networkRef.current.style.cursor = 'pointer';
      }
    });

    networkInstance.current.on('blurNode', () => {
      networkRef.current.style.cursor = 'default';
    });
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  const refreshData = () => {
    loadGraphData();
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      showPersons: true,
      showMissions: true,
      showSkills: true,
      showDomains: true,
      showMotivations: true,
      showRhythms: true,
      showCommunication: true,
      showConstraints: true,
      showGoals: true,
      showLanguages: true,
      showInterests: true,
      selectedPerson: 'all'
    });
  };

  const unselectAllNodes = () => {
    setFilters(prev => ({
      ...prev,
      showPersons: false,
      showMissions: false,
      showSkills: false,
      showDomains: false,
      showMotivations: false,
      showRhythms: false,
      showCommunication: false,
      showConstraints: false,
      showGoals: false,
      showLanguages: false,
      showInterests: false
    }));
  };

  // Get unique persons for the dropdown
  const getPersonOptions = () => {
    if (!graphData) return [];
    return graphData.nodes
      .filter(node => node.group === 'person')
      .map(node => ({ id: node.id, name: node.label }));
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 w-full h-[90vh]">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-orange-400">Network Visualization</h1>
          <p className="text-gray-400 text-sm">Interactive graph of missions, people, and relationships</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={refreshData}
            disabled={isLoading}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            onClick={goBackToDashboard}
            className="text-sm text-orange-400 hover:underline"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
        <div className="flex items-center gap-2">
          {isLoading && <div className="spinner w-4 h-4"></div>}
          <span className="text-sm text-gray-300">{status}</span>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="mb-4 p-4 bg-gray-700/30 rounded-lg">
        <div className="flex flex-wrap items-center gap-4 mb-3">
          <h3 className="text-sm font-semibold text-gray-400">Filters:</h3>
          <button 
            onClick={resetFilters}
            className="text-xs bg-gray-600 hover:bg-gray-500 px-2 py-1 rounded text-gray-300"
          >
            Select All
          </button>
          <button 
            onClick={unselectAllNodes}
            className="text-xs bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-gray-300"
          >
            Unselect All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Node Type Filters */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-400">Show Node Types:</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {[
                { key: 'showPersons', label: 'Persons', color: 'bg-blue-500' },
                { key: 'showMissions', label: 'Missions', color: 'bg-purple-500' },
                { key: 'showSkills', label: 'Skills', color: 'bg-green-500' },
                { key: 'showDomains', label: 'Domains', color: 'bg-yellow-500' },
                { key: 'showMotivations', label: 'Motivations', color: 'bg-red-500' },
                { key: 'showRhythms', label: 'Rhythms', color: 'bg-purple-400' },
                { key: 'showCommunication', label: 'Communication', color: 'bg-cyan-500' },
                { key: 'showConstraints', label: 'Constraints', color: 'bg-red-600' },
                { key: 'showGoals', label: 'Goals', color: 'bg-green-600' },
                { key: 'showLanguages', label: 'Languages', color: 'bg-violet-500' },
                { key: 'showInterests', label: 'Interests', color: 'bg-orange-500' }
              ].map(filter => (
                <label key={filter.key} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters[filter.key]}
                    onChange={(e) => handleFilterChange(filter.key, e.target.checked)}
                    className="w-3 h-3"
                  />
                  <div className={`w-3 h-3 rounded-full ${filter.color}`}></div>
                  <span className="text-gray-300">{filter.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Person Focus Filter */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-400">Focus on Person:</h4>
            <select
              value={filters.selectedPerson}
              onChange={(e) => handleFilterChange('selectedPerson', e.target.value)}
              className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-xs text-gray-300"
            >
              <option value="all">Show All Connections</option>
              {getPersonOptions().map(person => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Stats */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-400">Current View:</h4>
            <div className="text-xs text-gray-300">
              {filteredData ? (
                <>
                  <div>{filteredData.nodes.length} nodes</div>
                  <div>{filteredData.edges.length} relationships</div>
                </>
              ) : (
                <div>Loading...</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 p-3 bg-gray-700/30 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-400 mb-2">Legend:</h3>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span className="text-gray-300">Person</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-purple-500"></div>
            <span className="text-gray-300">Mission</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-gray-300">Skill/Tool</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            <span className="text-gray-300">Domain</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span className="text-gray-300">Motivation</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-purple-400"></div>
            <span className="text-gray-300">Rhythm</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-cyan-500"></div>
            <span className="text-gray-300">Communication</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-600"></div>
            <span className="text-gray-300">Constraint</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
            <span className="text-gray-300">Goal</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-violet-500"></div>
            <span className="text-gray-300">Language</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span className="text-gray-300">Interest</span>
          </div>
        </div>
      </div>

      {/* Network Container */}
      <div 
        ref={networkRef}
        className="w-full bg-gray-900 rounded-lg border border-gray-600"
        style={{ height: 'calc(100% - 280px)' }}
      >
        {!graphData && !isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <p>No graph data available</p>
              <button 
                onClick={refreshData}
                className="mt-2 text-orange-400 hover:underline"
              >
                Try loading data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VisualizationScreen;
